import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Animated, Easing, StyleSheet, View } from 'react-native';
import Text from 'design-system/Text';
import Button from 'design-system/Button';
import { rem } from 'design-system/values';
import IconRepeat from 'design-system/icons/IconRepeat';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import useForceSync from 'hooks/useForceSync';
import { selectIsSyncingManual } from 'reducers/selectors';
import { IconSettings } from 'design-system/icons/IconSettings';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import useIsTablet from 'hooks/useIsTablet';
import { useRemovePayTabExperience } from 'features/pay/hooks/useRemovePayTabExperience';
import OverviewAddButton from './OverviewAddButton';
import OverviewPayButton from './OverviewPayButton';
import { usePreviousLastSync } from './hooks/usePreviousLastSync';
const selectLastChange = (store) => store.user.lastChange;
const OverviewLastSynced = () => {
    const navigation = useMainStackNavigation();
    const showsPayButton = useRemovePayTabExperience();
    const isTablet = useIsTablet();
    const fullWidthButtons = showsPayButton && !isTablet;
    const lastSuccessfulSync = useAppSelector(selectLastChange);
    const isSyncingManual = useAppSelector(selectIsSyncingManual);
    const [isFetchingSyncStatus, forceSync] = useForceSync();
    const [spinValue] = useState(new Animated.Value(0));
    const isSpinning = isFetchingSyncStatus || isSyncingManual;
    const spinAnim = useRef(Animated.timing(spinValue, {
        toValue: 1,
        duration: 1000,
        easing: Easing.linear,
        useNativeDriver: true,
        isInteraction: false,
    }));
    const spin = useCallback(() => {
        spinValue.setValue(0);
        spinAnim.current.start(({ finished }) => {
            if (isSpinning && finished) {
                spin();
            }
        });
    }, [isSpinning, spinValue]);
    useEffect(() => {
        if (isSpinning) {
            spin();
        }
        else {
            spinValue.setValue(0);
            spinAnim.current.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSpinning]);
    const prevLastSync = usePreviousLastSync(lastSuccessfulSync);
    const formattedDateTime = useMemo(() => {
        const date = lastSuccessfulSync || prevLastSync;
        return date ? format(new Date(date), 'dd MMM h:mmaaa') : undefined;
    }, [prevLastSync, lastSuccessfulSync]);
    const onPressSync = useCallback(async () => {
        forceSync();
    }, [forceSync]);
    const onSettingsPress = useCallback(() => {
        navigation.navigate('Logins');
    }, []);
    const colors = useColors();
    return (<View style={styles.container}>
      <View style={fullWidthButtons
            ? [styles.buttonsContainer, styles.buttonsContainerFullWidth]
            : styles.buttonsContainer}>
        <OverviewAddButton flex={fullWidthButtons}/>
        <OverviewPayButton flex={fullWidthButtons}/>
        <Button brandBorder cards flex={fullWidthButtons} title="Sync" onPress={onPressSync} leftIcon={<Animated.View style={{
                transform: [
                    {
                        rotate: spinValue.interpolate({
                            inputRange: [0, 1],
                            outputRange: ['0deg', '360deg'],
                        }),
                    },
                ],
            }}>
              <IconRepeat width={16} height={16} color={colors.text.textOnBrand}/>
            </Animated.View>} containerStyle={styles.sync} leftIconContainerStyle={styles.iconLeft}/>
        <Button onPress={onSettingsPress} brandBorder cards title="" leftIcon={<IconSettings width={16} height={16} color={colors.text.textOnBrand}/>} containerStyle={styles.settings} leftIconContainerStyle={styles.settingsLeftIcon}/>
      </View>
      {!fullWidthButtons && (<View style={styles.date}>
          <Text TextThin-14 Gray numberOfLines={1}>
            {formattedDateTime}
          </Text>
        </View>)}
    </View>);
};
const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        marginVertical: rem(16),
        marginHorizontal: rem(16),
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    buttonsContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: rem(8),
        marginRight: rem(8),
    },
    buttonsContainerFullWidth: {
        flex: 1,
        marginRight: 0,
    },
    date: {
        flex: 1,
        alignItems: 'flex-end',
    },
    iconLeft: {
        paddingRight: rem(6),
    },
    sync: {
        paddingHorizontal: rem(10),
    },
    settings: {
        paddingHorizontal: rem(6),
        paddingVertical: rem(6),
    },
    settingsLeftIcon: {
        paddingRight: 0,
    },
});
export default OverviewLastSynced;
